import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { CheckCircle, Crown, Spinner } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { ConsensoMarketing } from "../components/ConsensoMarketing";
import { useBrandFont } from "../lib/brandFonts";

const inputCls = "w-full h-12 px-4 rounded-xl border border-zinc-700 bg-zinc-900 text-white text-sm placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-amber-400";

export default function FidelityJoin() {
  const { rid } = useParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);
  const [nome, setNome] = useState("");
  const [cognome, setCognome] = useState("");
  const [telefono, setTelefono] = useState("");
  const [email, setEmail] = useState("");
  const [consenso, setConsenso] = useState(false);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(null); // {card_url, customer_name}
  useBrandFont(info?.font || "");

  useEffect(() => {
    api.get(`/public/fidelity/join/${rid}`).then((r) => setInfo(r.data)).catch((e) => setError(apiError(e)));
  }, [rid]);

  const submit = async () => {
    if (!nome.trim()) { toast.error("Inserisci il tuo nome"); return; }
    if (telefono.replace(/\D/g, "").length < 6) { toast.error("Inserisci un numero di telefono valido"); return; }
    setSending(true);
    try {
      const r = await api.post(`/public/fidelity/join/${rid}`, {
        first_name: nome, last_name: cognome, phone: telefono,
        email, marketing_consent: consenso,
      });
      setDone(r.data);
    } catch (e) { toast.error(apiError(e)); }
    finally { setSending(false); }
  };

  const colore = info?.colore || "#18181b";

  if (error) return (
    <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-6 text-center">
      <p className="text-zinc-400 text-sm">{error}</p>
    </div>
  );
  if (!info) return (
    <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
      <p className="text-zinc-500 text-sm">Caricamento...</p>
    </div>
  );

  return (
    <div className="min-h-screen bg-zinc-950 flex flex-col items-center px-4 py-10" data-testid="fidelity-join-page" data-brand-font="">
      <div className="w-full max-w-sm space-y-5">
        <div className="text-center space-y-3">
          {info.logo_url ? (
            <img src={info.logo_url} alt={info.restaurant_name}
              className="h-16 w-16 rounded-2xl object-cover bg-white mx-auto" data-testid="join-logo" />
          ) : (
            <div className="h-16 w-16 rounded-2xl bg-amber-400 mx-auto flex items-center justify-center">
              <span className="font-display font-black text-zinc-900 text-2xl">{info.restaurant_name.charAt(0)}</span>
            </div>
          )}
          <div>
            <h1 className="font-display text-2xl font-black text-white" data-testid="join-title">
              {info.nome_programma}
            </h1>
            <p className="text-sm text-zinc-400 mt-1">{info.restaurant_name}</p>
          </div>
        </div>

        {done ? (
          <div className="rounded-2xl border border-zinc-800 bg-zinc-900 p-6 text-center space-y-4" data-testid="join-success">
            <CheckCircle size={44} weight="fill" className="text-emerald-400 mx-auto" />
            <div>
              <h2 className="font-display text-lg font-bold text-white">Benvenuto, {done.customer_name}!</h2>
              <p className="text-sm text-zinc-400 mt-1">La tua fidelity card è pronta: aprila e salvala nei preferiti del telefono.</p>
            </div>
            <a href={done.card_url} data-testid="join-open-card"
              className="block w-full h-12 rounded-xl bg-amber-400 text-zinc-900 text-sm font-black leading-[3rem] text-center hover:bg-amber-300 active:scale-95 transition">
              APRI LA TUA CARD
            </a>
          </div>
        ) : (
          <div className="rounded-2xl border border-zinc-800 p-5 space-y-4"
            style={{ background: `linear-gradient(160deg, ${colore} 0%, #09090b 140%)` }}
            data-testid="join-form">
            <p className="text-xs text-zinc-300 text-center">
              Iscriviti gratis: accumuli punti a ogni visita e sblocchi i premi.
              {info.livelli?.length > 1 && " Più punti accumuli, più alto il tuo livello."}
            </p>
            {info.livelli?.length > 0 && (
              <div className="flex justify-center gap-1.5 flex-wrap" data-testid="join-livelli">
                {info.livelli.map((l, i) => (
                  <span key={i} className="text-[10px] font-black uppercase tracking-widest px-2 py-1 rounded-full border border-white/25 text-white flex items-center gap-1"
                    data-testid={`join-livello-${i}`}>
                    <Crown size={10} weight="fill" style={{ color: l.colore === "#18181b" ? "#a1a1aa" : l.colore }} />
                    {l.nome}{l.da_punti > 0 ? ` · da ${l.da_punti} pt` : ""}
                  </span>
                ))}
              </div>
            )}
            <div className="grid grid-cols-2 gap-2">
              <input value={nome} onChange={(e) => setNome(e.target.value)} placeholder="Nome *"
                className={inputCls} data-testid="join-nome" />
              <input value={cognome} onChange={(e) => setCognome(e.target.value)} placeholder="Cognome"
                className={inputCls} data-testid="join-cognome" />
            </div>
            <input value={telefono} onChange={(e) => setTelefono(e.target.value)} placeholder="Telefono *"
              type="tel" className={inputCls} data-testid="join-telefono" />
            <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email (facoltativa)"
              type="email" className={inputCls} data-testid="join-email" />
            <div className="[&_label]:text-zinc-400 [&_a]:text-zinc-300">
              <ConsensoMarketing rid={rid} checked={consenso} onChange={setConsenso}
                testid="join-consenso" accentColor="#fbbf24" />
            </div>
            <button onClick={submit} disabled={sending} data-testid="join-submit"
              className="w-full h-12 rounded-xl bg-amber-400 text-zinc-900 text-sm font-black flex items-center justify-center gap-2 hover:bg-amber-300 active:scale-95 transition disabled:opacity-50">
              {sending && <Spinner size={16} className="animate-spin" />}
              ISCRIVITI E RICEVI LA CARD
            </button>
          </div>
        )}

        <p className="text-center text-[10px] text-zinc-600">Powered by Dishenda</p>
      </div>
    </div>
  );
}
